@file:Suppress("MagicNumber", "ReturnCount")

package top.wcpe.allincore.ui.runtime.canvas

import top.wcpe.allincore.dsl.ir.canvas.DrawBlobSpec
import top.wcpe.allincore.dsl.ir.canvas.DrawOpcodes
import kotlin.jvm.JvmStatic

/**
 * DrawBlob 字节流轻量扫描器。
 * 在不构造 IR 对象的前提下顺序前进字节，验证 opcode 合法性并记录顶层指令跳表。
 */
internal object BlobScanner {

    /** 扫描整段 DrawBlob 字节流，返回顶层指令 endOffset 跳表。 */
    @JvmStatic
    fun scan(blob: ByteArray?, canvasId: String?): IntArray {
        require(blob != null) { "blob 不能为 null" }
        if (blob.isEmpty()) return IntArray(0)
        if (blob.size < 2) {
            throw CanvasExecutionException(
                canvasId,
                -1,
                0,
                "DrawBlob 字节流损坏：缺少 u16 count 头",
            )
        }
        val buf = BlobReader(blob)
        buf.short // u16 count 头
        val ends = ArrayList<Int>()
        scanRange(buf, blob.size, canvasId, ends, true)
        if (buf.position() != blob.size) {
            throw CanvasExecutionException(
                canvasId,
                -1,
                buf.position(),
                "DrawBlob 扫描后剩余 ${blob.size - buf.position()} 字节未消费",
            )
        }
        return ends.toIntArray()
    }

    @Suppress("ThrowsCount")
    private fun scanRange(
        buf: BlobReader,
        endPos: Int,
        canvasId: String?,
        topLevelEnds: MutableList<Int>?,
        recordTop: Boolean,
    ) {
        while (buf.position() < endPos) {
            val instStart = buf.position()
            val op = buf.get().toInt() and 0xFF
            try {
                advanceInstruction(buf, op.toByte(), endPos, canvasId)
            } catch (e: IndexOutOfBoundsException) {
                throw CanvasExecutionException(canvasId, op, instStart, "DrawBlob 字节流损坏: ${e.message}", e)
            } catch (e: IllegalArgumentException) {
                throw CanvasExecutionException(canvasId, op, instStart, "DrawBlob 字节流损坏: ${e.message}", e)
            }
            if (recordTop) topLevelEnds?.add(buf.position())
        }
    }

    @Suppress("CyclomaticComplexMethod")
    private fun advanceInstruction(buf: BlobReader, op: Byte, endPos: Int, canvasId: String?) {
        when (op.toInt()) {
            DrawOpcodes.OP_FOR_END.toInt(), DrawOpcodes.OP_IF_END.toInt() -> return
            DrawOpcodes.OP_VAL_ASSIGN.toInt() -> {
                buf.get()
                skipOperand(buf)
            }
            DrawOpcodes.OP_FOR_BEGIN.toInt() -> {
                buf.get()
                buf.short
                val bodyLen = buf.short.toInt() and 0xFFFF
                val bodyEnd = buf.position() + bodyLen
                require(bodyEnd <= endPos) { "FOR body 越界: bodyEnd=$bodyEnd endPos=$endPos" }
                scanRange(buf, bodyEnd, canvasId, null, false)
                if (buf.position() < endPos && buf.get(buf.position()) == DrawOpcodes.OP_FOR_END) buf.get()
            }
            DrawOpcodes.OP_IF_BEGIN.toInt() -> {
                skipOperand(buf)
                val thenLen = buf.short.toInt() and 0xFFFF
                val elseLen = buf.short.toInt() and 0xFFFF
                val thenEnd = buf.position() + thenLen
                val elseEnd = thenEnd + elseLen
                require(elseEnd <= endPos) { "IF then/else 越界: elseEnd=$elseEnd endPos=$endPos" }
                scanRange(buf, thenEnd, canvasId, null, false)
                scanRange(buf, elseEnd, canvasId, null, false)
                if (buf.position() < endPos && buf.get(buf.position()) == DrawOpcodes.OP_IF_END) buf.get()
            }
            DrawOpcodes.OP_POLYGON.toInt() -> {
                val n = buf.get().toInt() and 0xFF
                repeat(n * 2 + 1) { skipOperand(buf) }
            }
            DrawOpcodes.OP_PATH.toInt() -> {
                val n = buf.get().toInt() and 0xFF
                repeat(n * 2 + 2) { skipOperand(buf) }
                buf.get()
            }
            DrawOpcodes.OP_PATH_BUILD.toInt() -> {
                val cmdCount = buf.get().toInt() and 0xFF
                repeat(cmdCount) {
                    val k = buf.get()
                    repeat(DrawBlobSpec.pathNodeOperandCount(k)) { skipOperand(buf) }
                }
                buf.get()
                skipOperand(buf)
                skipOperand(buf)
            }
            else -> repeat(DrawBlobSpec.expectedOperandCount(op)) { skipOperand(buf) }
        }
    }

    private fun skipOperand(buf: BlobReader) {
        val kind = buf.get().toInt() and 0xFF
        when {
            kind == DrawOpcodes.OPERAND_LITERAL.toInt() -> buf.position(buf.position() + 8)
            kind == DrawOpcodes.OPERAND_EXPR.toInt() ||
                kind == DrawOpcodes.OPERAND_STR.toInt() ||
                kind == DrawOpcodes.OPERAND_BYTES.toInt() -> {
                val len = buf.short.toInt() and 0xFFFF
                buf.position(buf.position() + len)
            }
            else -> throw IllegalArgumentException("未知操作数 kind: 0x${(kind).toString(16)}")
        }
    }
}
